/**
 * Auth adapter — hybrid: gated OTP send/confirm go through our server
 * (so the access-code check can't be bypassed), but session lifetime is
 * owned by the Supabase SDK (auto-refresh, persistence, cross-tab Web
 * Lock coordination, error classification).
 *
 * Flow on sign-in:
 *
 *   1. `sendXxxCode` → POST /v1/auth/send-otp     (server validates code)
 *   2. user enters OTP
 *   3. `verifyXxxCode` → POST /v1/auth/confirm-otp (server validates code,
 *                                                   returns Supabase
 *                                                   tokens)
 *   4. Adapter calls `supabase.auth.setSession(tokens)` — SDK takes over
 *
 * From step 4 onward, the SDK is the source of truth: it refreshes against
 * Supabase directly, writes/reads localStorage, and broadcasts session
 * changes across tabs. The adapter just translates SDK events into the
 * shape `auth-context.tsx` expects.
 *
 * See `./supabase-client.ts` for why this hybrid layout matters (PR #172
 * fallout). Note that anyone with an active session under the pre-SDK
 * `putty_auth` localStorage key will be bounced to the login screen
 * once on first load after this ships — there is no migration path.
 */
import { supabase } from "./supabase-client";
import { sendOtp, confirmOtp } from "@/api/auth-api";
import { ConfiguredLogger } from "@/lib/logger";
const logger = new ConfiguredLogger({ caller: "AuthAdapter" });
// ---------------------------------------------------------------------------
// User mapping
// ---------------------------------------------------------------------------
function mapSupabaseUser(user) {
    if (!user)
        return undefined;
    const metadata = user.user_metadata;
    return {
        id: user.id,
        email: user.email ?? undefined,
        phone: user.phone ?? undefined,
        displayName: metadata?.full_name ?? metadata?.name,
        avatarUrl: metadata?.avatar_url,
    };
}
// ---------------------------------------------------------------------------
// Post-confirm session handoff
// ---------------------------------------------------------------------------
/**
 * Hand server-issued tokens to the SDK. `setSession` validates them with
 * Supabase (one round trip to `/auth/v1/user`), stores them under our
 * configured storage key, and emits `SIGNED_IN` — which the subscribed
 * `onAuthStateChange` listener picks up and forwards to the auth context.
 */
async function adoptSession(tokens) {
    const { error } = await supabase.auth.setSession({
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken,
    });
    if (error) {
        // Failure here means the tokens our server just minted are already
        // rejected by Supabase — that's a real bug worth surfacing rather
        // than silently dropping the sign-in.
        throw error;
    }
}
// ---------------------------------------------------------------------------
// Adapter factory
// ---------------------------------------------------------------------------
export function createAuthAdapter() {
    const subscribe = (listener) => {
        // `onAuthStateChange` fires INITIAL_SESSION synchronously on subscribe
        // with whatever the SDK loaded from storage, then SIGNED_IN /
        // SIGNED_OUT / TOKEN_REFRESHED on subsequent transitions. The auth
        // context relies on the INITIAL_SESSION fire to flip its `isLoading`
        // flag, so we don't need a separate `getUser()` priming call.
        const { data: { subscription }, } = supabase.auth.onAuthStateChange((_event, session) => {
            listener(mapSupabaseUser(session?.user));
        });
        return () => subscription.unsubscribe();
    };
    const getUser = async () => {
        const { data } = await supabase.auth.getSession();
        return mapSupabaseUser(data.session?.user);
    };
    const sendPhoneCode = async (phone, password) => {
        await sendOtp({ type: "phone", phone, password });
    };
    const sendEmailCode = async (email, password) => {
        await sendOtp({ type: "email", email, password });
    };
    const verifyPhoneCode = async (phone, password, token) => {
        const session = await confirmOtp({ type: "phone", phone, password, token });
        await adoptSession(session);
    };
    const verifyEmailCode = async (email, password, token) => {
        const session = await confirmOtp({ type: "email", email, password, token });
        await adoptSession(session);
    };
    const signOut = async () => {
        await supabase.auth.signOut();
    };
    const getAccessToken = async () => {
        // `getSession` triggers a refresh internally if the cached token is
        // within the SDK's expiry margin, so callers always get a token
        // that's safe to send.
        const { data } = await supabase.auth.getSession();
        return data.session?.access_token;
    };
    const refreshUser = async () => {
        // Used after the add-identifier flow to pull the updated identifier
        // off the new JWT. `refreshSession` rotates the tokens via the
        // SDK's internal Web Lock so it's safe even if a background refresh
        // is in flight.
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
            // Keep the existing session in storage — a transient refresh
            // failure (network blip, 5xx) shouldn't log the user out. The
            // SDK's auto-refresh timer will retry on its own. Warn so the
            // failure is observable rather than silently dropping the
            // post-add-identifier UI update.
            logger.warn("Forced session refresh failed; keeping current session", {
                error: error.message,
            });
            const { data: current } = await supabase.auth.getSession();
            return mapSupabaseUser(current.session?.user);
        }
        return mapSupabaseUser(data.session?.user);
    };
    return {
        getUser,
        subscribe,
        sendPhoneCode,
        sendEmailCode,
        verifyPhoneCode,
        verifyEmailCode,
        signOut,
        getAccessToken,
        refreshUser,
    };
}
